/**
 * The vocabulary of "Correo", shared by the inbox, the reader and the composer.
 *
 * Types, labels, and the small pure functions that turn a stored row into what a
 * person reads. They live here rather than in the workspace because the entity
 * panels on a candidate and on a company show the same threads through a
 * different door, and two copies of "who is this conversation with" is one bug
 * waiting for a mailbox alias.
 */

// ── Rows, as the data plane returns them ─────────────────────────────

export type EmailDirection = "inbound" | "outbound";
export type EmailStatus = "received" | "sent" | "failed";
export type ThreadStatus = "open" | "closed";
export type LinkedEntityType = "none" | "talent" | "company" | "prospect" | "application";
export type LinkedBy = "auto" | "manual";
/** Whether there is a person on the other end at all — see backend/src/services/email-kind.ts. */
export type ThreadKind = "person" | "automated" | "broadcast";

export interface ThreadRow {
    id: string;
    subject?: string | null;
    subject_key?: string | null;
    participants?: string[] | null;
    /** Null on rows written before drizzle/0051, which read as `person`. */
    kind?: ThreadKind | null;
    entity_type?: LinkedEntityType | null;
    entity_id?: string | null;
    employer_key?: string | null;
    contact_id?: string | null;
    linked_by?: LinkedBy | null;
    /** The vacancy the subject names, guessed at ingest. */
    job_id?: string | null;
    /** The candidature registered from this conversation, once somebody did. */
    application_id?: string | null;
    /** The admin who will answer, by email (drizzle/0054). Null = anybody. */
    owner?: string | null;
    /** When to come back to it. Cleared by ingest when they reply. */
    follow_up_at?: string | null;
    /**
     * Whether their last message asks for an answer (drizzle/0055).
     *
     * False for "muchas gracias por su respuesta" and for a Gmail reaction —
     * decided at ingest by backend/src/services/email-reply.ts, and set by
     * hand from the reader when a person disagrees. Null on rows written
     * before the column existed, which read as `true`.
     */
    needs_reply?: boolean | null;
    /** The first words of the newest message, for the list row. */
    last_snippet?: string | null;
    /** Who is on the other end, by name, from their From header (drizzle/0056). */
    counterpart_name?: string | null;
    status?: ThreadStatus | null;
    unread?: boolean | null;
    message_count?: number | null;
    last_message_at?: string | null;
    last_direction?: EmailDirection | null;
    last_inbound_at?: string | null;
    last_outbound_at?: string | null;
    /** X-GM-THRID — what makes "Abrir en Gmail" possible. */
    gmail_thread_id?: string | null;
}

export interface MessageRow {
    id: string;
    thread_id: string;
    direction?: EmailDirection | null;
    status?: EmailStatus | null;
    message_id?: string | null;
    from_email?: string | null;
    from_name?: string | null;
    to_emails?: string[] | null;
    cc_emails?: string[] | null;
    subject?: string | null;
    body_text?: string | null;
    body_html?: string | null;
    body_snippet?: string | null;
    has_attachments?: boolean | null;
    attachment_names?: string[] | null;
    sent_at?: string | null;
    actor?: string | null;
    error?: string | null;
}

/** What one poll did — the POST half of functions/sync-inbox.ts. */
export interface SyncResult {
    ok: boolean;
    fetched: number;
    stored: number;
    threads_created: number;
    linked: number;
    /**
     * Conversations already stored that gained a link on this run — the pass
     * that gives "Sin asignar" threads another look once the person on the other
     * end exists in the database. Distinct from `linked`, which is decided as a
     * message arrives.
     */
    relinked: number;
    /** Conversations that gained the vacancy their subject names. */
    vacancies_recognised: number;
    /** Stored conversations moved to "Automáticos" on Gmail's word. */
    reclassified: number;
    truncated: boolean;
    resynced: boolean;
    /** No IMAP session was opened: one happened inside the server's floor. */
    skipped: boolean;
    mailbox: string;
}

/** What the mailbox itself reports — see functions/sync-inbox.ts. */
export interface MailboxStatus {
    receiving: boolean;
    sending: boolean;
    from: string | null;
    mailbox: string;
    last_polled_at: string | null;
    last_message_at: string | null;
    last_error: string | null;
    messages_ingested: number;
}

// ── Labels ───────────────────────────────────────────────────────────

export const ENTITY_LABEL: Record<LinkedEntityType, string> = {
    // "Asignar" is taken, and taken by the other meaning: a conversation is
    // *assigned* to a person on the team who will answer it. What it is
    // *connected* to is the candidate or the employer it is about. Two
    // meanings of one word, side by side in the same tab bar, is how you get
    // a screen nobody can read.
    none: "Sin conectar",
    talent: "Candidato",
    company: "Empresa",
    prospect: "Empresa que contrata",
    application: "Candidatura"
};

export const ENTITY_COLOR: Record<LinkedEntityType, string> = {
    none: "grayLighter",
    talent: "greenLighter",
    company: "blueLighter",
    prospect: "cyanLighter",
    application: "purpleLighter"
};

/**
 * What a conversation is, said as a person would say it.
 *
 * "Automático" was the value in the database wearing a label's clothes: it
 * told you which enum branch the row was on and nothing about the mail. What
 * is actually in there is invoices, appointment tokens, calendar notices,
 * bounces and whoever you have silenced — mail a machine wrote. So the label
 * says that.
 */
export const KIND_LABEL: Record<ThreadKind, string> = {
    person: "Conversación",
    automated: "Lo escribió una máquina",
    broadcast: "Envío masivo nuestro"
};

/** The same, short enough for a chip on a list row. */
export const KIND_CHIP: Record<ThreadKind, string> = {
    person: "conversación",
    automated: "máquina",
    broadcast: "envío masivo"
};

export const STATUS_LABEL: Record<EmailStatus, string> = {
    received: "Recibido",
    sent: "Enviado",
    failed: "No salió"
};

/** The filters across the top of the inbox. "Para responder" is the queue. */
export type InboxTab = "unread" | "waiting" | "mine" | "today" | "all" | "unlinked" | "automated" | "closed";

export const INBOX_TABS: { id: InboxTab; label: string }[] = [
    // Order runs from the work you owe to the archive you consult, the same
    // shape "Empresas que contratan" uses. Everything before "Automáticos" is
    // conversations with people; that tab is where the machines went.
    { id: "unread", label: "Sin leer" },
    { id: "waiting", label: "Esperan respuesta" },
    // "Asignadas a mí" and "Para hoy" are the two tabs ownership and follow-ups add
    // (drizzle/0054): what I said I would answer, and what somebody put down
    // to pick up today.
    { id: "mine", label: "Asignadas a mí" },
    { id: "today", label: "Para hoy" },
    { id: "unlinked", label: "Sin conectar" },
    { id: "all", label: "Todas" },
    { id: "automated", label: "Correo de máquinas" },
    // "Cerrar" already means something else in this product — closing a
    // selection process, which the candidates screen does — and a word that
    // means two things in one panel means neither. Gmail's word, for the
    // thing that is exactly Gmail's gesture.
    { id: "closed", label: "Archivadas" }
];

/**
 * What each tab holds, in one line, under the tabs.
 *
 * Not decoration: every one of these existed only in somebody's head, and a
 * filter whose name you have to guess at is a filter you do not press. The
 * line shows for whichever tab is open, which is the moment you would be
 * wondering.
 */
export const INBOX_TAB_HINT: Record<InboxTab, string> = {
    unread: "Correo que todavía no has abierto.",
    waiting: "Escribieron ellos, y lo que dijeron pide una respuesta que aún no has enviado.",
    mine: "Las que dijiste que contestas tú.",
    today: "Las que alguien puso para retomar hoy.",
    unlinked: "No sabemos de qué candidato o empresa son. Se conectan desde la conversación.",
    all: "Todas las conversaciones con personas, contestadas o no.",
    automated: "Facturas, avisos, calendario, rebotes y los remitentes que has silenciado. No cuentan como pendientes y no se pueden conectar a nadie.",
    closed: "Archivadas en el panel. En Gmail siguen donde estaban."
};

/**
 * Which tab a thread belongs to, decided in one place.
 *
 * The queues are conversations with people only. A machine's thread is kept
 * and readable under "Automáticos" and counts towards nothing: it cannot owe
 * a reply and nobody can be assigned to it. Closed outranks everything, so a
 * closed notification is filed with the rest of the closed ones.
 */
export function inTab(thread: ThreadRow, tab: InboxTab, me: string | null = null, now: Date = new Date()): boolean {
    if (tab === "closed") return thread.status === "closed";
    if (thread.status === "closed") return false;
    const person = isPersonThread(thread);
    if (tab === "automated") return !person;
    if (!person) return false;
    if (tab === "unread") return Boolean(thread.unread);
    if (tab === "waiting") return isWaitingOnUs(thread, now);
    if (tab === "mine") return Boolean(me) && (thread.owner ?? "").toLowerCase() === me;
    if (tab === "today") return isDueForFollowUp(thread, now);
    // Nobody on the thread is nobody to assign: a broadcast is caught above,
    // but a thread whose participants were stripped for being our own
    // addresses is a `person` row with an empty list.
    if (tab === "unlinked") return entityTypeOf(thread) === "none" && (thread.participants ?? []).length > 0;
    return true;
}

// ── Reading a thread at a glance ─────────────────────────────────────

/**
 * Whether we owe this conversation a reply.
 *
 * The last message came from them and we have not answered. Derived rather than
 * stored, deliberately: it is a fact about the messages, and a stored copy of it
 * is a copy that goes stale the moment somebody replies from Gmail instead.
 */
export function isWaitingOnUs(thread: ThreadRow, now: Date = new Date()): boolean {
    if (!isPersonThread(thread) || thread.status === "closed") return false;
    if (thread.last_direction !== "inbound") return false;
    // Their last word was "muchas gracias" — an answer is not owed. The
    // column is written at ingest and can be corrected by hand in the reader;
    // null is a row from before drizzle/0055 and means "yes, it is owed".
    if (thread.needs_reply === false) return false;
    // Snoozed. Somebody read it and said "not until Thursday", and a queue
    // that keeps showing it has taken that decision away from them. It comes
    // back on its own day, in "Para hoy" and here.
    if (isSnoozed(thread, now)) return false;
    return true;
}

/** Put down until a date still ahead of us. Gmail calls it snoozing. */
export function isSnoozed(thread: ThreadRow, now: Date = new Date()): boolean {
    if (!thread.follow_up_at) return false;
    const due = new Date(thread.follow_up_at);
    if (Number.isNaN(due.getTime())) return false;
    const endOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1).getTime();
    return due.getTime() >= endOfToday;
}

/**
 * Whether a reminder on this conversation has come due.
 *
 * "Due" is "on or before the end of today", in calendar days like the rest
 * of the panel: a reminder set for this morning is still today's work at
 * four in the afternoon.
 */
export function isDueForFollowUp(thread: ThreadRow, now: Date = new Date()): boolean {
    if (!thread.follow_up_at || thread.status === "closed") return false;
    const due = new Date(thread.follow_up_at);
    if (Number.isNaN(due.getTime())) return false;
    const endOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1).getTime();
    return due.getTime() < endOfToday;
}

/** "volver hoy", "volver en 3 días", "volver hace 2 días" — the reminder as the card says it. */
export function followUpLabel(thread: ThreadRow, now: Date = new Date()): string | null {
    if (!thread.follow_up_at) return null;
    const due = new Date(thread.follow_up_at);
    if (Number.isNaN(due.getTime())) return null;
    const startOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
    const days = Math.round((startOfDay(due) - startOfDay(now)) / 86_400_000);
    if (days <= 0) return days === 0 ? "volver hoy" : `volver hace ${-days} día${days === -1 ? "" : "s"}`;
    if (days === 1) return "volver mañana";
    return `volver en ${days} días`;
}

/**
 * The tab a link to one conversation opens on: one that is certain to hold it.
 *
 * A linked conversation is often already read or already filed, which no queue
 * tab contains, and landing on a tab that does not hold it hands the selection
 * straight to the top of that tab. Closed outranks everything (see `inTab`), a
 * machine's mail is only ever under "Correo de máquinas", and "Todas" holds
 * every other conversation with a person.
 */
export function tabFor(thread: ThreadRow): InboxTab {
    if (thread.status === "closed") return "closed";
    return isPersonThread(thread) ? "all" : "automated";
}

/** Rows written before `kind` existed carry null and are conversations. */
export function kindOf(thread: ThreadRow): ThreadKind {
    return thread.kind === "automated" || thread.kind === "broadcast" ? thread.kind : "person";
}

/** Whether there is somebody on the other end. Only these can owe a reply or be assigned. */
export function isPersonThread(thread: ThreadRow): boolean {
    return kindOf(thread) === "person";
}

/**
 * How long a conversation has been waiting, in whole days.
 *
 * Null when it is not waiting on us. Days rather than hours because that is the
 * unit an operator plans in, and the same reason `_prospects_shared` counts
 * calendar days: a reply owed since yesterday morning is "1 día" all day today,
 * not a number that changes while you look at it.
 */
export function daysWaiting(thread: ThreadRow): number | null {
    if (!isWaitingOnUs(thread) || !thread.last_inbound_at) return null;
    const since = new Date(thread.last_inbound_at);
    if (Number.isNaN(since.getTime())) return null;
    const startOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
    return Math.max(0, Math.round((startOfDay(new Date()) - startOfDay(since)) / 86_400_000));
}

/** "hace 3 días" — a past date is only useful here as a distance from today. */
export function sinceLabel(iso: string | null | undefined): string {
    if (!iso) return "sin fecha";
    const when = new Date(iso);
    if (Number.isNaN(when.getTime())) return "sin fecha";
    const startOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
    const days = Math.round((startOfDay(new Date()) - startOfDay(when)) / 86_400_000);
    if (days <= 0) {
        // Today is the one case where the hour is worth showing: an inbox where
        // everything from the last eight hours reads "hoy" cannot be scanned.
        return when.toLocaleTimeString("es-ES", { hour: "2-digit", minute: "2-digit" });
    }
    if (days === 1) return "ayer";
    if (days < 30) return `hace ${days} días`;
    const months = Math.floor(days / 30);
    return months === 1 ? "hace 1 mes" : `hace ${months} meses`;
}

/** The full stamp, for the reader, where the exact moment is the point. */
export function fullStamp(iso: string | null | undefined): string {
    if (!iso) return "";
    const when = new Date(iso);
    if (Number.isNaN(when.getTime())) return "";
    return when.toLocaleString("es-ES", {
        day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit"
    });
}

/**
 * Who the conversation is with, in one line.
 *
 * The participants, never our own address — the backend already excludes it, and
 * a thread listed as being with `hola@sustentalent.com` would be every thread.
 * Falls back to the subject's absence rather than to an empty string, because a
 * blank row in a list reads as a rendering bug.
 */
export function threadWith(thread: ThreadRow): string {
    const people = (thread.participants ?? []).filter(Boolean);
    if (people.length === 0) return "sin destinatario";
    if (people.length === 1) return people[0];
    return `${people[0]} +${people.length - 1}`;
}

/**
 * Who a row leads with: the name we know, then the name they sign with,
 * then the address. Never the subject — that is the line below.
 */
export function threadPerson(thread: ThreadRow, linkedName?: string | null): string {
    return (linkedName ?? "").trim()
        || (thread.counterpart_name ?? "").trim()
        || threadWith(thread);
}

export function threadTitle(thread: ThreadRow): string {
    const subject = (thread.subject ?? "").trim();
    return subject || "(sin asunto)";
}

export function entityTypeOf(thread: ThreadRow): LinkedEntityType {
    const type = thread.entity_type;
    return type && type in ENTITY_LABEL ? type : "none";
}

/**
 * Quoting, from the module the backend uses too.
 *
 * Re-exported here so the views have one import for "the vocabulary of Correo",
 * and imported from `config/email_quoting.ts` rather than reimplemented because
 * the reader and the ingest must agree about where a reply ends. They did not
 * once: the reader treated `body_snippet` as "the body minus quotation" when it
 * is that *and* whitespace-collapsed *and* cut to 180 characters, so every
 * message over a short paragraph rendered truncated.
 */
export { stripQuotedReply, hasQuotedReply, buildSnippet } from "../email_quoting";

/**
 * The Gmail URL for a conversation, from its X-GM-THRID.
 *
 * Gmail addresses a thread by the **hex** form of that id, which arrives as a
 * 19-digit decimal string — past `Number.MAX_SAFE_INTEGER`, so parsing it as a
 * number loses the low bits and produces a link to a conversation that does not
 * exist. Hence BigInt. The backend has the same function for its own use; this
 * is the browser's copy, and `gmail-labels.test.ts` pins the shared expectation.
 *
 * `#all/` rather than `#inbox/`, so an archived conversation still opens.
 */
export function gmailThreadUrl(gmailThreadId: string | null | undefined): string | null {
    const raw = (gmailThreadId ?? "").trim();
    if (!/^\d+$/.test(raw)) return null;
    try {
        return `https://mail.google.com/mail/u/0/#all/${BigInt(raw).toString(16)}`;
    } catch {
        return null;
    }
}

// ── Where a conversation lives ───────────────────────────────────────

/** The query parameter "Correo" opens on a conversation from. */
export const CONVERSATION_PARAM = "conversacion";

/**
 * The address of one conversation: the mailbox, open on it.
 *
 * Everywhere else in the panel a conversation opens in a side panel over the
 * screen you are on (see ConversationPanel.tsx), and that panel writes this to
 * the address bar while it is open — so Back closes it, and a reload or a
 * copied link lands in Correo on the same conversation rather than on
 * whatever tops the queue.
 */
export function conversationUrl(threadId: string): string {
    return `/correo?${CONVERSATION_PARAM}=${encodeURIComponent(threadId)}`;
}

/**
 * "Open this one in Correo", said to a Correo screen that is already mounted.
 *
 * Navigating to `conversationUrl` from inside the panel does not remount the
 * mailbox when the mailbox is the screen underneath — same route, same
 * component — so the parameter it reads once, on arrival, is never read
 * again. This event is the second way in: the mailbox listens for it and lands
 * on the conversation it names. `detail` is the thread id.
 */
export const OPEN_CONVERSATION_EVENT = "sustentalent:open-conversation";

/** Newest conversation first, which is the only order an inbox has. */
export function compareThreads(a: ThreadRow, b: ThreadRow): number {
    return (b.last_message_at ?? "").localeCompare(a.last_message_at ?? "");
}

/** Oldest message first: a conversation is read downwards. */
export function compareMessages(a: MessageRow, b: MessageRow): number {
    return (a.sent_at ?? "").localeCompare(b.sent_at ?? "");
}

// ── Talking to the backend ───────────────────────────────────────────

/**
 * Call one of this feature's custom functions.
 *
 * The same shape `ProspectContactsPanel` uses for `find-contacts`: the Rebase
 * client has no typed accessor for custom functions, so the token and the base
 * URL are assembled by hand. Centralised here so the three call sites do not
 * each grow their own copy of the error handling.
 */
export async function callFunction<T>(
    client: any,
    name: string,
    body?: Record<string, unknown>
): Promise<T> {
    const token = await client?.resolveToken?.();
    const base = `${client?.baseUrl ?? ""}${client?.apiPath ?? "/api"}`;
    const res = await fetch(`${base}/functions/${name}`, {
        method: body ? "POST" : "GET",
        headers: {
            ...(body ? { "Content-Type": "application/json" } : {}),
            ...(token ? { Authorization: `Bearer ${token}` } : {})
        },
        body: body ? JSON.stringify(body) : undefined
    });
    const json = await res.json().catch(() => ({}));
    if (!res.ok) {
        // The backend's message when it has one: these are written for the
        // operator ("«x» no es una dirección válida"), and replacing them with
        // "Error 400" throws away the only useful part.
        throw new Error((json as any)?.error || `Error ${res.status}`);
    }
    return json as T;
}

/**
 * Opening a conversation is reading it — here and in Gmail.
 *
 * Shared by the mailbox and the side panel, which is the one place a
 * conversation opens outside it: a conversation read from a candidate's record
 * and still bold in Correo is an inbox that disagrees with the person who just
 * read it.
 *
 * The Gmail half is fired and deliberately not awaited — it opens an IMAP
 * connection, and hanging the caller on that would make the panel feel slower
 * than the thing it is replacing. The promise is the panel's own write; the
 * caller decides what to do if that fails.
 */
export async function markThreadRead(client: any, threadId: string): Promise<void> {
    void callFunction(client, "gmail-sync-thread", { thread_id: threadId, seen: true })
        .catch(() => { /* the panel's own state is the one that matters here */ });
    await client?.data?.collection("email-threads").update(threadId, { unread: false });
}

/**
 * The document an inbound message is rendered into, inside a sandboxed iframe.
 *
 * `body_html` was already sanitized on the way in (services/email-sanitize.ts),
 * so this is the second layer rather than the only one — and the second layer is
 * the one that holds if the first ever has a gap. The sandbox attribute is set
 * on the iframe itself and carries **no** `allow-scripts` and **no**
 * `allow-same-origin`, which together mean: no script may run, and nothing in
 * the document can reach this origin's cookies, storage or DOM even if one did.
 *
 * The `<base target="_blank">` is what stops a link navigating the iframe in
 * place; `Content-Security-Policy` in a meta tag is a third belt, blocking any
 * remote fetch the sanitizer did not already strip.
 */
export function readerDocument(html: string): string {
    return `<!doctype html>
<html><head>
<meta charset="utf-8">
<meta http-equiv="Content-Security-Policy" content="default-src 'none'; style-src 'unsafe-inline'; img-src data:; font-src data:">
<base target="_blank">
<style>
  html, body { margin: 0; padding: 0; }
  body {
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Arial, sans-serif;
    font-size: 14px; line-height: 1.6; color: #1e293b; word-break: break-word;
  }
  a { color: #0d9488; }
  img { max-width: 100%; }
  table { max-width: 100%; }
  blockquote { margin: 0 0 0 12px; padding-left: 12px; border-left: 2px solid #cbd5e1; color: #64748b; }
  .email-image-placeholder { color: #94a3b8; font-size: 12px; }
</style>
</head><body>${html}</body></html>`;
}
